import pluralize from 'pluralize';
import { Attachment, Chunk, Lesson } from '../../../../../../types/Assemblage';
import { LessonType } from 'edrolo/enums/LessonType';
import { AttachmentCategory } from '../../../../../../enums/Attachments';
import { enums } from 'edrolo-ui';
import { formatDuration } from '../../utils/utils';
import { Metadatum, MetadatumCategory } from '../../types/Metadata';

const categoryOrder = {
  [MetadatumCategory.SLIDES]: 1,
  [MetadatumCategory.LESSON_SLIDES]: 2,
  [MetadatumCategory.VIDEO]: 3,
  [MetadatumCategory.STUDY_NOTES]: 4,
  [MetadatumCategory.ACTIVITY]: 5,
  [MetadatumCategory.QUESTION_SET]: 6,
  [MetadatumCategory.WORKSHEET]: 7,
  [MetadatumCategory.FRAMEWORK]: 8,
  [MetadatumCategory.ASSESSMENT]: 9,
  [MetadatumCategory.PRAC]: 10,
  [MetadatumCategory.RISK_ASSESSMENT]: 11,
  [MetadatumCategory.UNIT_PLAN]: 12,
  [MetadatumCategory.TEXTBOOK]: 13,
  [MetadatumCategory.JEDDLE_STUDY_GUIDE]: 14,
  [MetadatumCategory.JEDDLE_QUIZ]: 15,
  [MetadatumCategory.OTHER]: 16,
};

export const extractLessonAttachments = (attachments: Attachment[]) => {
  const lessonContentAttachments: Attachment[] = attachments.filter(
    (attachment: Attachment) =>
      attachment.category === AttachmentCategory.LESSON_SLIDES
  );

  const attachmentContentItems: Attachment[] = attachments
    .filter(
      (attachment: Attachment) =>
        attachment.category !== AttachmentCategory.LESSON_SLIDES
    )
    .sort((a, b) => {
      return categoryOrder[a.category] - categoryOrder[b.category];
    });

  return { lessonContentAttachments, attachmentContentItems };
};

export const getLessonMetadata = (
  lesson: Lesson,
  activityChunks: Chunk[],
  attachmentContentItems: Attachment[]
): Array<Metadatum> => {
  const metadata: Metadatum[] = [];
  const sameCategoryCount: Record<string, number> = {};

  const addToMetadata = (item: Metadatum) => {
    if (sameCategoryCount[item.category] !== undefined) {
      sameCategoryCount[item.category]++;
      // Update the existing item with the new count
      const existingItem = metadata.find((m) => m.category === item.category);
      if (existingItem) {
        existingItem.count = sameCategoryCount[item.category];
        existingItem.title = pluralize(existingItem.title, existingItem.count);
      }
    } else {
      sameCategoryCount[item.category] = 1;
      metadata.push(item);
    }
  };

  if (lesson.chunk_count > 0) {
    switch (lesson.lesson_type) {
      case LessonType.SLIDES:
        metadata.push({
          title: 'Slide Lesson',
          count: lesson.chunks.length,
          iconName: enums.Icons.LESSON,
          color: enums.ColorHue.BLUE,
          category: MetadatumCategory.SLIDES,
        });
        break;
      case LessonType.VIDEO:
        metadata.push({
          title: 'Video Lesson',
          count: formatDuration(lesson.total_duration),
          iconName: enums.Icons.VIDEO_LESSON,
          color: enums.ColorHue.BLUE,
          category: MetadatumCategory.VIDEO,
        });
        break;
      case LessonType.PRAC:
        metadata.push({
          title: 'Slide Prac',
          count: lesson.chunks.length,
          iconName: enums.Icons.FLASK,
          color: enums.ColorHue.AMBER,
          category: MetadatumCategory.PRAC,
        });
        break;
      case LessonType.JEDDLE:
        if (lesson.total_duration > 0) {
          metadata.push({
            title: 'Video Lesson',
            count: formatDuration(lesson.total_duration),
            iconName: enums.Icons.VIDEO_LESSON,
            color: enums.ColorHue.BLUE,
            category: MetadatumCategory.VIDEO,
          });
        }
        if (lesson.chunks.some((chunk) => chunk.has_pdf)) {
          metadata.push({
            title: 'Study Guide',
            count: null,
            iconName: enums.Icons.STUDY_NOTES,
            color: enums.ColorHue.INDIGO,
            category: MetadatumCategory.JEDDLE_STUDY_GUIDE,
          });
        }
        if (lesson.chunks.some((chunk) => chunk.has_question)) {
          metadata.push({
            title: 'Quiz',
            count: null,
            iconName: enums.Icons.QUESTION,
            color: enums.ColorHue.GREEN,
            category: MetadatumCategory.JEDDLE_QUIZ,
          });
        }
        break;
      default:
        return [];
    }
  }

  if (lesson.question_set) {
    metadata.push({
      title: pluralize('Question', lesson.question_set.question_count),
      count: lesson.question_set.question_count,
      iconName: enums.Icons.QUESTION,
      color: enums.ColorHue.GREEN,
      category: MetadatumCategory.QUESTION_SET,
    });
  }
  if (activityChunks.length > 0) {
    metadata.push({
      title: pluralize('Activity', activityChunks.length),
      count: activityChunks.length > 1 ? activityChunks.length : null,
      iconName: enums.Icons.ACTIVITY,
      color: enums.ColorHue.PINK,
      category: MetadatumCategory.ACTIVITY,
    });
  }

  if (attachmentContentItems.length > 0) {
    attachmentContentItems.forEach((attachment: Attachment) => {
      switch (attachment.category) {
        case AttachmentCategory.STUDY_NOTES:
          return addToMetadata({
            title: 'Study notes',
            count: null,
            iconName: enums.Icons.STUDY_NOTES,
            color: enums.ColorHue.BLUE,
            category: MetadatumCategory.STUDY_NOTES,
          });
        case AttachmentCategory.WORKSHEET:
          return addToMetadata({
            title: 'Worksheet',
            count: null,
            iconName: enums.Icons.WORKSHEET,
            color: enums.ColorHue.YELLOW,
            category: MetadatumCategory.WORKSHEET,
          });
        case AttachmentCategory.FRAMEWORK:
          return addToMetadata({
            title: 'Framework',
            count: null,
            iconName: enums.Icons.FRAMEWORK,
            color: enums.ColorHue.YELLOW,
            category: MetadatumCategory.FRAMEWORK,
          });
        case AttachmentCategory.ASSESSMENT:
          return addToMetadata({
            title: 'Assessment',
            count: null,
            iconName: enums.Icons.PENCIL_CHECK,
            color: enums.ColorHue.TEAL,
            category: MetadatumCategory.ASSESSMENT,
          });
        case AttachmentCategory.RISK_ASSESSMENT:
          return addToMetadata({
            title: 'Risk Assessment',
            count: null,
            iconName: enums.Icons.RISK_ASSESS,
            color: enums.ColorHue.RED,
            category: MetadatumCategory.RISK_ASSESSMENT,
          });
        case AttachmentCategory.UNIT_PLAN:
          return addToMetadata({
            title: 'Unit Plan',
            count: null,
            iconName: enums.Icons.UNIT_PLAN,
            color: enums.ColorHue.TEAL,
            category: MetadatumCategory.UNIT_PLAN,
          });
        case AttachmentCategory.TEXTBOOK:
          return addToMetadata({
            title: 'Textbook',
            count: null,
            iconName: enums.Icons.TEXTBOOK,
            color: enums.ColorHue.INDIGO,
            category: MetadatumCategory.TEXTBOOK,
          });
        default:
          return addToMetadata({
            title: 'Other attachment',
            count: 1,
            iconName: enums.Icons.ATTACHMENT,
            color: enums.ColorTheme.PRIMARY,
            category: MetadatumCategory.OTHER,
          });
      }
    });
  }

  return metadata.sort(
    (a, b) => categoryOrder[a.category] - categoryOrder[b.category]
  );
};
